import { type LoginFlow, ResponseError } from '@ory/client-fetch';
import { Login } from '@ory/elements-react/theme';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

import { oryComponentOverrides } from '@apps/web/components/auth/ory_components';
import { useOry } from '@apps/web/hooks/ory';
import { sanitizeReturnToPath } from '@apps/web/lib/post_login_redirect';

export const Route = createFileRoute('/_auth/sign-in')({
    beforeLoad: async ({ context, search }) => {
        const { sessionState } = context;
        if (sessionState?.session) {
            const returnToPath = sanitizeReturnToPath(
                search.return_to,
                window.location.origin,
            );
            if (returnToPath) {
                // Already signed in with a captured destination: send them
                // there instead of dropping it at `/`. `href` takes the
                // re-constructed same-origin absolute URL, which handles an
                // arbitrary path + query that a typed `to` cannot express, and
                // makes the router reload the document rather than route to it.
                // Throwing hands the navigation to the router, which aborts
                // this match instead of loading chunks its own redirect would
                // then abort mid-flight (a rejected dynamic import that
                // surfaces as a crash in the error boundary).
                throw redirect({
                    href: `${window.location.origin}${returnToPath}`,
                });
            }
            throw redirect({ to: '/' });
        }
    },
    component: SignInPage,
    validateSearch: z.object({
        flow: z.string().optional(),
        return_to: z.string().optional(),
    }),
});

// Kratos answers 4xx for a flow it will not serve: 410 once it has expired,
// 404 for an unknown id, 403 when another browser's CSRF cookie owns it. A 5xx
// or network failure says nothing about the flow, so it is not replaced.
function isDeadFlowError(error: unknown): boolean {
    return (
        error instanceof ResponseError &&
        error.response.status >= 400 &&
        error.response.status < 500
    );
}

function SignInPage() {
    const { flow: flowId, return_to: returnTo } = Route.useSearch();
    const router = useRouter();
    const returnToPath = useMemo(
        () => sanitizeReturnToPath(returnTo, window.location.origin),
        [returnTo],
    );
    const [flow, setFlow] = useState<LoginFlow | null>(null);
    const [flowError, setFlowError] = useState<string | null>(null);
    const { oryClient, oryElementsConfiguration, normalizeOryPayload } =
        useOry();
    const loginElementsConfiguration = useMemo(() => {
        return {
            ...oryElementsConfiguration,
            project: {
                ...oryElementsConfiguration.project,
                registration_enabled: false,
            },
        };
    }, [oryElementsConfiguration]);

    // A flow id in the URL can be long dead: Kratos expires login flows after
    // an hour, and people bookmark or reopen the sign-in link. Such a flow is
    // replaced with a fresh one. A flow this page created failing to load
    // means sign-in itself is broken, so that shows the error instead of
    // minting flows in a loop.
    const createdFlowIdsRef = useRef(new Set<string>());

    useEffect(() => {
        // Cleared when the inputs change or the page unmounts, so a slow
        // request from an earlier run cannot navigate someone who has moved
        // on or overwrite what the current run shows.
        let isCurrent = true;
        (async () => {
            try {
                setFlowError(null);
                if (flowId && returnTo && !returnToPath) {
                    await router.navigate({
                        to: '/sign-in',
                        search: { flow: flowId },
                        replace: true,
                    });
                    return;
                }
                if (flowId) {
                    try {
                        const flow = normalizeOryPayload(
                            await oryClient.getLoginFlow({ id: flowId }),
                        );
                        if (isCurrent) {
                            setFlow(flow);
                        }
                        return;
                    } catch (error: unknown) {
                        if (
                            !isDeadFlowError(error) ||
                            createdFlowIdsRef.current.has(flowId)
                        ) {
                            throw error;
                        }
                    }
                }
                const flow = normalizeOryPayload(
                    await oryClient.createBrowserLoginFlow(
                        returnToPath ? { returnTo: returnToPath } : undefined,
                    ),
                );
                if (!isCurrent) {
                    return;
                }
                createdFlowIdsRef.current.add(flow.id);
                router.navigate({
                    to: '/sign-in',
                    search: {
                        flow: flow.id,
                        ...(returnToPath ? { return_to: returnToPath } : {}),
                    },
                    // Replacing a dead flow keeps it out of history, so Back
                    // does not return to it.
                    replace: Boolean(flowId || (returnTo && !returnToPath)),
                });
            } catch (error: unknown) {
                if (!isCurrent) {
                    return;
                }
                console.error(error);
                setFlow(null);
                setFlowError(
                    'Unable to load sign-in right now. Please try again.',
                );
            }
        })();
        return () => {
            isCurrent = false;
        };
    }, [
        flowId,
        normalizeOryPayload,
        oryClient,
        returnTo,
        returnToPath,
        router,
    ]);

    // Fallback navigation. On a normal submit, Ory's <Login> hard-navigates to
    // `flow.return_to` itself; this only fires if React re-renders first.
    useEffect(() => {
        if (flow?.state === 'passed_challenge') {
            if (returnToPath) {
                // Re-construct the same-origin absolute URL so the assignment
                // can never be read as a protocol-relative external target.
                window.location.href = `${window.location.origin}${returnToPath}`;
            } else {
                router.navigate({ to: '/' });
            }
        }
    }, [flow, returnToPath, router]);

    if (!flow) {
        if (flowError) {
            return (
                <div className="mx-auto max-w-xl px-6 py-12 text-center">
                    <h1 className="text-xl font-semibold">
                        Sign-in unavailable
                    </h1>
                    <p className="text-muted-foreground mt-3 text-sm">
                        {flowError}
                    </p>
                </div>
            );
        }

        return null;
    }

    return (
        <div className="ory-root w-full max-w-xl">
            <Login
                flow={flow}
                config={loginElementsConfiguration}
                components={oryComponentOverrides}
            />
        </div>
    );
}
